import type { ReactNode } from "react";
import { formatPrice } from "@/lib/currency";
import { ProspectiveMemberPrice } from "@/hooks/useFriendsUpsell";
import { Check } from "lucide-react";

interface FriendsUpsellToggleProps {
  pricing: ProspectiveMemberPrice;
  seats: number;
  currency?: string | null;
  joinMembership: boolean;
  /** User has credits that would otherwise apply — reassure they're kept. */
  hasCredits?: boolean;
  /**
   * What declining actually costs today, after credits (same waterfall as the
   * Book button). Falls back to the pre-credits price when omitted.
   */
  declineTotal?: number;
  /** Credits the decline path would spend (0 = none). */
  declineCreditsUsed?: number;
  onChange: (next: boolean) => void;
}

/**
 * Two-option choice at checkout: join Nimi Friends (membership + member-price
 * ticket in one payment) or just buy the ticket. Both options state the total
 * the user actually pays today; no "you're paying more" framing.
 *
 * The two options are one grouped choice ("How would you like to pay?") so
 * standalone banners like Bring a Friend never read as a third option.
 */
const FriendsUpsellToggle = ({
  pricing,
  seats,
  currency,
  joinMembership,
  hasCredits = false,
  declineTotal,
  declineCreditsUsed = 0,
  onChange,
}: FriendsUpsellToggleProps) => {
  const seatLabel = seats > 1 ? `${seats} tickets` : "ticket";
  const declinePay = declineTotal ?? pricing.declineTotal;
  // Whole pounds only in this section — no decimal points (£18, not £18.00).
  const fmt = (n: number) => formatPrice(n, currency, { compact: true });
  const creditsSuffix =
    declineCreditsUsed > 0
      ? ` + ${declineCreditsUsed} ${declineCreditsUsed === 1 ? "credit" : "credits"}`
      : "";
  const discountLabel =
    pricing.friendsDiscountPct > 0 ? ` (${pricing.friendsDiscountPct}% off)` : "";

  const Option = ({
    selected,
    title,
    subtitle,
    onSelect,
  }: {
    selected: boolean;
    title: string;
    subtitle: ReactNode;
    onSelect: () => void;
  }) => (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 transition-colors ${
        selected ? "bg-primary/5" : "hover:bg-muted/40"
      }`}
    >
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
            selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
          }`}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          <span className="block text-xs text-muted-foreground mt-0.5">{subtitle}</span>
        </span>
      </div>
    </button>
  );

  return (
    <div className="mb-3">
      <p className="text-xs font-medium text-muted-foreground mb-1.5">How would you like to pay?</p>
      <div className="rounded-lg border border-border overflow-hidden divide-y divide-border bg-card">
        <Option
          selected={joinMembership}
          onSelect={() => onChange(true)}
          title={`Join ${pricing.packageName} — ${fmt(pricing.joinTotal)} today`}
          subtitle={
            <>
              <span className="block">
                Today: {fmt(pricing.memberUnitPrice * seats)} {seatLabel}
                {discountLabel} + {fmt(pricing.membershipCost)} first month of{" "}
                {pricing.packageName}
              </span>
              <span className="block">
                Ongoing: {fmt(pricing.membershipCost)}/month ·{" "}
                {pricing.friendsDiscountPct}% off tickets · Cancel anytime
              </span>
            </>
          }
        />
        <Option
          selected={!joinMembership}
          onSelect={() => onChange(false)}
          title={`Just the ${seats > 1 ? "tickets" : "ticket"} — ${fmt(declinePay)}${creditsSuffix} today`}
          subtitle={`${pricing.packageName} pay ${fmt(
            pricing.memberUnitPrice,
          )} for this event.`}
        />
      </div>
      {hasCredits && joinMembership && (
        <p className="text-xs text-muted-foreground px-1 mt-1.5">Your credits stay in your account.</p>
      )}
    </div>
  );
};

export default FriendsUpsellToggle;
